import { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../../config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faCalendarAlt, // Changed to CalendarAlt to match the design request
} from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import { IoIosSearch } from "react-icons/io";
import LoadingSpinner from "../../components/LoadingSpinner";
import "../../components/skeletonRow.css";

// New Imports for Bootstrap Date Range Picker
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";
// Ensure you have this imported if your global styles depend on it, 
// otherwise the local style tag below handles the specific input design.
// import "../../index.css"; 

const ManageNotificationHistory = () => {
  const [notificationHistory, setNotificationHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem("authToken");
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 100;

  const [filters, setFilters] = useState({
    title: "",
    description: "",
    start_date: null,
    end_date: null,
  });

  const [appliedFilters, setAppliedFilters] = useState({
    title: "",
    description: "",
    start_date: null,
    end_date: null,
  });

  // Define Ranges (Matched to your reference code)
  const ranges = {
    "This Week": [moment().startOf("isoWeek"), moment().endOf("isoWeek")],
    "Today": [moment(), moment()],
    "Yesterday": [moment().subtract(1, "days"), moment().subtract(1, "days")],
    "Last 7 Days": [moment().subtract(6, "days"), moment()],
    "Last 30 Days": [moment().subtract(29, "days"), moment()],
    "This Month": [moment().startOf("month"), moment().endOf("month")],
    "Last Month": [
      moment().subtract(1, "month").startOf("month"),
      moment().subtract(1, "month").endOf("month"),
    ],
    "This Year": [moment().startOf("year"), moment().endOf("year")],
    "Last Year": [
      moment().subtract(1, "year").startOf("year"),
      moment().subtract(1, "year").endOf("year"),
    ],
  };

  const fetchNotifications = async () => {
    try {
      setLoading(true);

      // Format dates for API
      const formatDateForAPI = (date) => {
        return date ? moment(date).format("YYYY-MM-DD") : "";
      };

      const params = {
        limit,
        page,
        title: appliedFilters.title,
        description: appliedFilters.description,
        start_date: formatDateForAPI(appliedFilters.start_date),
        end_date: formatDateForAPI(appliedFilters.end_date),
      };

      // Remove empty parameters
      Object.keys(params).forEach((key) => {
        if (params[key] === "" || params[key] === null) {
          delete params[key];
        }
      });

      const response = await axios.get(`${API_URL}history/list`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      const { data, meta } = response.data.data;
      setNotificationHistory(data);
      setPageCount(meta.pageCount);
      setTotal(meta.total);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [page, appliedFilters]);

  useEffect(() => {
    const handleClearFilters = () => {
      handleClear();
    };

    window.addEventListener('clearNotificationHistoryFiltersEvent', handleClearFilters);

    return () => {
      window.removeEventListener('clearNotificationHistoryFiltersEvent', handleClearFilters);
    };
  }, []);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pageCount) {
      setPage(newPage);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Callback when date range is applied
  const handleDateCallback = (event, picker) => {
    const start = picker.startDate;
    const end = picker.endDate;

    setFilters((prev) => ({
      ...prev,
      start_date: start,
      end_date: end,
    }));
  };

  const handleSearch = () => {
    setAppliedFilters(filters);
    setPage(1);
  };

  const handleClear = () => {
    const clearedFilters = {
      title: "",
      description: "",
      start_date: null,
      end_date: null,
    };
    setFilters(clearedFilters);
    setAppliedFilters(clearedFilters);
    setPage(1);
  };

  const hasAnyFilter = () => {
    return (
      filters.title !== "" ||
      filters.description !== "" ||
      filters.start_date !== null ||
      filters.end_date !== null
    );
  };

  // Helper function to display the date in the input box as "DD-MM-YYYY - DD-MM-YYYY"
  const getDateRangePickerValue = () => {
    if (!filters.start_date || !filters.end_date) {
      return "";
    }
    const start = moment(filters.start_date);
    const end = moment(filters.end_date);

    if (!start.isValid() || !end.isValid()) return "";

    return `${start.format("DD-MM-YYYY")} - ${end.format("DD-MM-YYYY")}`;
  };

  // Fix date display for table rows
  const formatDisplayDate = (dateString) => {
    if (!dateString) return "";

    // Explicitly parse backend format
    const parsed = moment(dateString, "DD-MM-YYYY hh:mm A", true);

    if (!parsed.isValid()) {
      return "";
    }

    return parsed.format("DD-MM-YYYY hh:mm A");
  };

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  return (
    <div className="page-header-fixed page-sidebar-fixed mt-5">
      <div className="panel panel-default with-rounded-corner with-shadow">
        <div className="d-flex align-items-center justify-content-between">
          <div className="panel-heading border-0">
            <h4 className="panel-title">Manage Notification History</h4>
          </div>
          <div className="me-3">
            <span style={{ fontSize: "0.8rem" }} className="me-2">
              {start} to {end} of {total}
            </span>
            {hasAnyFilter() && (
              <button
                className="btn btn-danger btn-sm ms-2"
                onClick={handleClear}
                title="Clear all filters"
              >
                Clear Filter
              </button>
            )}
            <button
              className="btn btn-default btn-sm ms-2"
              title="Previous"
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            &nbsp;
            <button
              className="btn btn-default btn-sm"
              title="Next"
              onClick={() => handlePageChange(page + 1)}
              disabled={page === pageCount}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        </div>
        {(loading || notificationHistory.length > 0) ? (
          <>
            <style>
              {`
                  .sticky-history-table thead tr:first-child th {
                    position: sticky;
                    top: 0;
                    z-index: 20;
                    background: #f8f9fa;
                  }

                  .sticky-history-table thead tr:nth-child(2) th {
                    position: sticky;
                    top: 30px; 
                    z-index: 19;
                    background: white;
                  }

                  .sticky-history-container {
                    max-height: calc(100vh - 165px);
                    overflow: auto;
                  }

                  .sticky-history-table th {
                    vertical-align: top;
                  }
                  
                  /* Date range picker specific styling to match design */
                  .daterangepicker-input {
                    cursor: pointer;
                    background: #fff !important;
                  }
                  .input-group-addon {
                    display: flex;
                    align-items: center;
                    padding: 0 10px;
                    background: #eee;
                    border: 1px solid #ced4da;
                    border-left: none;
                    border-top-right-radius: 4px;
                    border-bottom-right-radius: 4px;
                  }
                `}
            </style>

            <div className="sticky-history-container">
              <table className="table m-b-0 sticky-history-table">
                <colgroup>
                  <col style={{ width: "5%" }} />
                  <col style={{ width: "20%" }} />
                  <col style={{ width: "45%" }} />
                  <col style={{ width: "30%" }} />
                </colgroup>
                <thead>
                  {/* First Header Row - Column Titles */}
                  <tr>
                    <th style={{ textAlign: "center" }}>#</th>
                    <th>Title</th>
                    <th>Description</th>
                    <th>Date Time</th>
                  </tr>

                  {/* Second Header Row - Search Filters */}
                  <tr>
                    <th></th>
                    <th>
                      <div className="d-flex">
                        <input
                          type="text"
                          name="title"
                          value={filters.title}
                          onChange={handleFilterChange}
                          className="form-control form-control-sm"
                          placeholder="Filter title"
                        />
                        <button
                          type="button"
                          className="btn btn-default btn-sm ms-2"
                          onClick={handleSearch}
                        >
                          <IoIosSearch />
                        </button>
                      </div>
                    </th>
                    <th>
                      <div className="d-flex">
                        <input
                          type="text"
                          name="description"
                          value={filters.description}
                          onChange={handleFilterChange}
                          className="form-control form-control-sm"
                          placeholder="Filter description"
                        />
                        <button
                          type="button"
                          className="btn btn-default btn-sm ms-2"
                          onClick={handleSearch}
                        >
                          <IoIosSearch />
                        </button>
                      </div>
                    </th>
                    <th>
                      <div className="d-flex align-items-start">
                        {/* IMPLEMENTING BOOTSTRAP DATE RANGE PICKER WITH INPUT DESIGN */}
                        <DateRangePicker
                          initialSettings={{
                            autoUpdateInput: false,
                            locale: {
                              format: "DD-MM-YYYY",
                              cancelLabel: "Clear",
                            },
                            ranges: ranges,
                            startDate: filters.start_date
                              ? moment(filters.start_date)
                              : undefined,
                            endDate: filters.end_date
                              ? moment(filters.end_date)
                              : undefined,
                          }}
                          onApply={handleDateCallback}
                        >
                          <div
                            className="input-group"
                            style={{ width: "100%" }}
                          >
                            <input
                              type="text"
                              className="form-control form-control-sm daterangepicker-input"
                              value={getDateRangePickerValue()}
                              placeholder="Select Date Range"
                              readOnly
                            />
                            <span className="input-group-addon">
                              <FontAwesomeIcon icon={faCalendarAlt} />
                            </span>
                          </div>
                        </DateRangePicker>

                        <button
                          type="button"
                          className="btn btn-default btn-sm ms-2"
                          onClick={handleSearch}
                        >
                          <IoIosSearch />
                        </button>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    Array.from({ length: 10 }).map((_, skeletonIndex) => (
                      <tr key={`skeleton-${skeletonIndex}`}>
                        <td style={{ textAlign: "center" }}>
                          <div className="skeleton-text" style={{ width: "20px", margin: "0 auto" }}></div>
                        </td>
                        <td>
                          <div className="skeleton-text" style={{ width: "80%" }}></div>
                        </td>
                        <td>
                          <div className="skeleton-text" style={{ width: "90%" }}></div>
                        </td>
                        <td>
                          <div className="skeleton-text" style={{ width: "70%" }}></div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    notificationHistory.map((post, index) => (
                      <tr key={post.id}>
                        <td
                          style={{ textAlign: "center", verticalAlign: "middle" }}
                        >
                          {(page - 1) * limit + index + 1}
                        </td>
                        <td style={{ wordWrap: "break-word", whiteSpace: "normal" }}>
                          {post.title}
                        </td>
                        <td style={{ wordWrap: "break-word", whiteSpace: "normal" }}>
                          {post.description}
                        </td>
                        <td style={{ whiteSpace: "nowrap" }}>
                          {formatDisplayDate(post.created_on)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="text-center p-3">No records found</div>
        )}
      </div>

      <a href="#" data-click="scroll-top" className="btn-scroll-top fade">
        <i className="ti-arrow-up"></i>
      </a>
    </div>
  );
};

export default ManageNotificationHistory;